import { Selector, Pill, useActiveTheme } from '@care/react-component-lib';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Typography,
  type Theme,
} from '@mui/material';
import { Icon24UtilityChevron } from '@care/react-icons';
import { loadFilters } from '@/utils/filtersStorage';
import { Language, SEOSeniorCareSubServices } from '@/__generated__/globalTypes';

interface PillProps {
  label: string;
  value: string | Language | SEOSeniorCareSubServices;
}
interface MultiSelectPillsProps {
  handleOnChangeEvent: (value: any[]) => void;
  selectedValues: Array<Language | string | SEOSeniorCareSubServices>;
  pillOptions: PillProps[];
  category: Category;
  sectionName: string;
}

export type Category =
  | 'SENIOR_CARE_CAN_HELP_WITH'
  | 'SENIOR_CARE_PROFESSIONAL_SKILLS'
  | 'PREFERENCES'
  | 'LANGUAGES_SPOKEN'
  | 'CHILD_AGE_RANGES'
  | 'CHILD_CARE_PROFESSIONAL_SKILLS'
  | 'HOME_CARE_CAN_HELP_WITH';

const MultiSelectPills = ({
  handleOnChangeEvent,
  selectedValues,
  pillOptions,
  category,
  sectionName,
}: MultiSelectPillsProps) => {
  const { isEmbraceTheme } = useActiveTheme();
  const localStorageFilters = loadFilters();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const seniorCareProfessionalSkillsMap: { [key: string]: string } = {
    CERTIFIED_NURSING_ASSISTANT: 'certifiedNursingAssistant',
    SENIOR_CARE_CPR_TRAINED: 'seniorCareCprTrained',
    HOME_HEALTH_AIDE: 'homeHealthAid',
    REGISTERED_NURSE: 'registeredNurse',
  };
  const preferencesMap: { [key: string]: string } = {
    COMFORTABLE_WITH_PETS: 'comfortableWithPets',
    HAS_CAR: 'hasCar',
    NON_SMOKER: 'nonSmoker',
  };

  // select all pill options that are currently in local storage
  const isSelected = (value: string, section: Category): boolean => {
    switch (section) {
      case 'SENIOR_CARE_CAN_HELP_WITH':
        return localStorageFilters?.seniorCareSubServices?.includes(value);
      case 'SENIOR_CARE_PROFESSIONAL_SKILLS':
        return (
          localStorageFilters?.[seniorCareProfessionalSkillsMap[value]] ||
          (value === SEOSeniorCareSubServices.DEMENTIA &&
            localStorageFilters?.seniorCareSubServices?.includes(SEOSeniorCareSubServices.DEMENTIA))
        );
      case 'PREFERENCES':
        return localStorageFilters?.[preferencesMap[value]];
      case 'LANGUAGES_SPOKEN':
        return localStorageFilters?.languages?.includes(value);
      case 'CHILD_AGE_RANGES':
        return localStorageFilters?.childCareAgeGroups?.includes(value);
      case 'CHILD_CARE_PROFESSIONAL_SKILLS':
        return localStorageFilters?.childCareQualifications?.includes(value);
      case 'HOME_CARE_CAN_HELP_WITH':
        return localStorageFilters?.housekeepingSubServices?.includes(value);
      default:
        return false;
    }
  };

  const pills = (
    <Selector
      onChange={(values: string[]) => handleOnChangeEvent(values)}
      horizontal
      value={selectedValues}
      name={category}
      sx={(theme: Theme) => ({
        '& > li': {
          margin: theme.spacing(0.5, 1, 0.5, 0),
        },
      })}>
      {pillOptions.map(({ label, value }) => {
        return (
          <Pill
            label={label}
            value={value}
            selected={isSelected(value, category)}
            key={value}
            sx={{
              '& label': {
                borderRadius: isEmbraceTheme ? 6 : undefined,
              },
            }}
          />
        );
      })}
    </Selector>
  );

  const mobileView = (
    <>
      <Box mb={1}>
        <Typography variant="h3">{sectionName}</Typography>
      </Box>
      {pills}
    </>
  );

  const desktopView = (
    <Accordion elevation={0} defaultExpanded>
      <AccordionSummary
        expandIcon={<Icon24UtilityChevron />}
        sx={{ padding: 0, width: 'fit-content' }}>
        <Typography variant="h3" mr={1}>
          {sectionName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={(theme) => ({
          padding: theme.spacing(0, 0, 0, 0.5),
        })}>
        {pills}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box component="section" mb={2}>
      {isMobile ? mobileView : desktopView}
    </Box>
  );
};

export { MultiSelectPills };
